import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, Clock, XCircle, CalendarDays, DollarSign, AlertTriangle, Wallet } from 'lucide-react';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, color }) => (
  <Card className="p-3 border-slate-200 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}1a`, color }}><Icon className="w-5 h-5" /></div>
    <div className="min-w-0"><div className="text-2xl font-extrabold text-slate-900 leading-none tabular-nums">{value}</div><div className="text-[11px] text-slate-500 mt-0.5">{label}</div></div>
  </Card>
);

// Reusable End-of-Day snapshot. Renders tech-scoped or admin (all) data.
export default function EodSnapshot({ snap }) {
  if (!snap) return null;
  const j = snap.jobs || {};
  const c = snap.collected || {};
  const methods = Object.entries(c.byMethod || {});
  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-500">
        {snap.scope === 'tech' ? `${snap.tech || 'Technician'} · ` : 'All technicians · '}{snap.date}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat icon={CalendarDays} label="Scheduled" value={j.scheduled ?? 0} color="#64748b" />
        <Stat icon={CheckCircle2} label="Completed" value={j.completed ?? 0} color="#10b981" />
        <Stat icon={Clock} label="Not done" value={j.remaining ?? 0} color="#f59e0b" />
        <Stat icon={XCircle} label="Cancelled" value={j.cancelled ?? 0} color="#ef4444" />
      </div>

      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-slate-900"><DollarSign className="w-4 h-4 text-[#8dd110]" />Money collected today</div>
          <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{money(c.total)}</div>
        </div>
        {methods.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-3">
            {methods.map(([m, v]) => <Badge key={m} className="bg-slate-100 text-slate-700 border-0">{m}: {money(v)}</Badge>)}
          </div>
        ) : <div className="text-xs text-slate-400 mt-2">No payments recorded today.</div>}
        <div className="text-[11px] text-slate-400 mt-2">{c.count || 0} payment(s)</div>
      </Card>

      {(snap.pastDue || snap.unpaid) && (
        <div className="grid grid-cols-2 gap-2.5">
          <Card className="p-3 border-red-200 bg-red-50/50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></div>
            <div><div className="text-xl font-extrabold text-red-700 tabular-nums">{money(snap.pastDue?.amount)}</div><div className="text-[11px] text-slate-500">Past due ({snap.pastDue?.count || 0})</div></div>
          </Card>
          <Card className="p-3 border-amber-200 bg-amber-50/50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
            <div><div className="text-xl font-extrabold text-amber-700 tabular-nums">{money(snap.unpaid?.amount)}</div><div className="text-[11px] text-slate-500">Accounts not paid ({snap.unpaid?.count || 0})</div></div>
          </Card>
        </div>
      )}

      {j.remainingList?.length > 0 && (
        <Card className="p-4 border-slate-200">
          <div className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" />Not completed ({j.remainingList.length})</div>
          <div className="space-y-1.5">
            {j.remainingList.map((w) => (
              <div key={w.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                <div className="min-w-0"><span className="font-semibold text-slate-800">{w.customer || '—'}</span><span className="text-slate-400"> · {w.service || ''}</span></div>
                <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] flex-shrink-0">{w.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {j.completedList?.length > 0 && (
        <Card className="p-4 border-slate-200">
          <div className="font-bold text-slate-900 mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Completed ({j.completedList.length})</div>
          <div className="space-y-1.5">
            {j.completedList.map((w) => (
              <div key={w.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                <div className="min-w-0"><span className="font-semibold text-slate-800">{w.customer || '—'}</span><span className="text-slate-400"> · {w.service || ''}</span></div>
                <span className="text-[11px] text-slate-400 flex-shrink-0">{w.time}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
